import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, CalendarDays, Users, Clock, ArrowRight, Upload, CheckCircle2, QrCode, Download, ExternalLink, MapPin } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { ReminderPreferences } from "../../components/ReminderPreferences";
import { AgendaSettings } from "../../components/AgendaSettings";

const statusColor = { submitted: "default", in_review: "secondary", completed: "outline", confirmed: "default", cancelled: "destructive" };

export default function Dashboard() {
  const { user, checkAuth, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [bookingUrl, setBookingUrl] = useState("");
  const photoRef = useRef(null);
  const [checkinDone, setCheckinDone] = useState(null);

  const doCheckin = async () => {
    try {
      const { data } = await api.post("/checkin");
      setCheckinDone(data.checked_in_at);
      toast.success("Checked in — the team has been notified and is getting ready for you");
    } catch (err) {
      toast.error(formatApiError(err, "Check-in failed"));
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/team/${user.user_id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newId = (data.photo_url || "").split("/").pop();
      if (newId) setUser({ ...user, photo_file_id: newId });
      toast.success("Profile photo updated — clients will see it when booking");
      checkAuth();
    } catch (err) {
      toast.error(formatApiError(err, "Photo upload failed"));
    } finally {
      e.target.value = "";
    }
  };

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  useEffect(() => {
    if (data?.role === "admin") {
      api.get("/qr/booking", { responseType: "blob" })
        .then((r) => {
          setQrUrl(URL.createObjectURL(r.data));
          setBookingUrl(r.headers["x-booking-url"] || "");
        })
        .catch(() => {});
    }
  }, [data?.role]);

  const downloadQr = async () => {
    try {
      const r = await api.get("/qr/booking", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "laronta-booking-qr.png";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download QR code");
    }
  };

  if (!data) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  const isStaff = data.role === "staff" || data.role === "admin";

  return (
    <div data-testid="dashboard-page" className="space-y-8 animate-fade-up">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">{isStaff ? "Staff overview" : "Client portal"}</p>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tight mt-1" data-testid="dashboard-greeting">
          Welcome, {user?.name?.split(" ")[0]}
        </h1>
      </div>

      {isStaff ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="staff-stats">
            {[
              { label: "Total Clients", value: data.total_clients, icon: Users },
              { label: "Intakes Submitted", value: data.total_intakes, icon: FileText },
              { label: "In Review", value: data.intakes_in_review, icon: Clock },
              { label: "Appointments Today", value: data.appointments_today, icon: CalendarDays },
            ].map((s) => (
              <Card key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-11 h-11 rounded-md bg-primary/10 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold font-heading">{s.value ?? 0}</p>
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card data-testid="profile-photo-card">
            <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center gap-6">
              {user?.photo_file_id ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${user.photo_file_id}`} alt="Your profile"
                  className="w-24 h-24 rounded-md object-cover border border-border" data-testid="profile-photo-preview" />
              ) : (
                <div className="w-24 h-24 rounded-md bg-primary/10 flex items-center justify-center font-heading text-4xl font-semibold text-primary border border-border" data-testid="profile-photo-placeholder">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>
              )}
              <div>
                <h2 className="font-heading text-xl font-semibold">Your Profile Photo</h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">
                  This photo is shown to clients on the public booking page when they choose their tax pro, and on the public team page.
                </p>
                <Button variant="outline" className="mt-3" onClick={() => photoRef.current?.click()} data-testid="profile-photo-upload-button">
                  <Upload className="w-4 h-4 mr-1" /> {user?.photo_file_id ? "Change Photo" : "Upload Photo"}
                </Button>
                <input ref={photoRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="profile-photo-input" onChange={uploadPhoto} />
              </div>
            </CardContent>
          </Card>
          <AgendaSettings />
          {data.role === "admin" && (
            <Card data-testid="booking-qr-card">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-primary" /> Share Your Booking Page
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row sm:items-center gap-6">
                {qrUrl ? (
                  <img src={qrUrl} alt="QR code linking to the public booking page" data-testid="booking-qr-image"
                    className="w-40 h-40 border border-border rounded-md bg-white p-2" />
                ) : (
                  <div className="w-40 h-40 border border-border rounded-md bg-muted animate-pulse" />
                )}
                <div>
                  <p className="text-sm text-muted-foreground max-w-md">
                    Clients scan this code to open your public booking page directly — print it for the office or share it digitally.
                  </p>
                  <p className="text-sm font-medium mt-2 text-primary" data-testid="booking-qr-url">{bookingUrl || `${window.location.origin}/book`}</p>
                  <div className="flex flex-wrap gap-3 mt-4">
                    <Button onClick={downloadQr} data-testid="booking-qr-download">
                      <Download className="w-4 h-4 mr-1" /> Download PNG
                    </Button>
                    <Button variant="outline" asChild>
                      <a href="/book" target="_blank" rel="noreferrer" data-testid="booking-qr-open-link">
                        <ExternalLink className="w-4 h-4 mr-1" /> Open Booking Page
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card data-testid="recent-intakes-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-xl">Recent Intakes</CardTitle>
                <Button asChild variant="ghost" size="sm"><Link to="/app/intake" data-testid="view-intakes-link">View all <ArrowRight className="w-3 h-3 ml-1" /></Link></Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data.recent_intakes || []).length === 0 && <p className="text-sm text-muted-foreground">No intakes yet.</p>}
                {(data.recent_intakes || []).map((i) => (
                  <div key={i.intake_id || i.user_id} className="flex items-center justify-between border-b border-border pb-3 last:border-0" data-testid={`intake-row-${i.user_id}`}>
                    <div>
                      <p className="text-sm font-medium">{i.client_name}</p>
                      <p className="text-xs text-muted-foreground">{i.email}</p>
                    </div>
                    <Badge variant={statusColor[i.status] || "default"} className="capitalize">{i.status?.replace("_", " ")}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card data-testid="upcoming-appointments-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-xl">Upcoming Appointments</CardTitle>
                <Button asChild variant="ghost" size="sm"><Link to="/app/appointments" data-testid="view-appointments-link">View all <ArrowRight className="w-3 h-3 ml-1" /></Link></Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data.upcoming_appointments || []).length === 0 && <p className="text-sm text-muted-foreground">No upcoming appointments.</p>}
                {(data.upcoming_appointments || []).map((a) => (
                  <div key={a.appointment_id} className="flex items-center justify-between border-b border-border pb-3 last:border-0" data-testid={`appt-row-${a.appointment_id}`}>
                    <div>
                      <p className="text-sm font-medium">{a.client_name} · {a.team_member_name}</p>
                      <p className="text-xs text-muted-foreground">{a.date} at {a.time_slot}</p>
                    </div>
                    <Badge variant={statusColor[a.status] || "default"} className="capitalize">{a.status}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card data-testid="recent-checkins-card">
              <CardHeader><CardTitle className="font-heading text-xl">Recent Check-Ins</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {(data.recent_checkins || []).length === 0 && <p className="text-sm text-muted-foreground">No check-ins yet.</p>}
                {(data.recent_checkins || []).map((c, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-border pb-3 last:border-0" data-testid={`checkin-row-${i}`}>
                    <div>
                      <p className="text-sm font-medium">{c.client_name}</p>
                      <p className="text-xs text-muted-foreground">{c.client_email}</p>
                    </div>
                    <Badge variant="secondary" data-testid={`checkin-time-${i}`}>
                      {c.checked_in_at?.slice(5, 16).replace("T", " ")}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <>
          <Card data-testid="checkin-card" className="bg-primary text-primary-foreground border-primary">
            <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="font-heading text-xl font-semibold">At the office?</h2>
                <p className="text-sm text-primary-foreground/75 mt-1 max-w-md">
                  Let the team know you've arrived — they get notified instantly and will get ready to serve you.
                </p>
              </div>
              {checkinDone ? (
                <Badge className="bg-secondary text-secondary-foreground shrink-0" data-testid="checkin-done-badge">
                  Checked in at {checkinDone.slice(11, 16)} UTC
                </Badge>
              ) : (
                <Button onClick={doCheckin} className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shrink-0" data-testid="checkin-button">
                  <MapPin className="w-4 h-4 mr-1" /> I'm Here — Check In
                </Button>
              )}
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="client-stats">
            <Card data-testid="intake-status-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary" />
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Intake Status</p>
                </div>
                <p className="mt-3 text-xl font-heading font-semibold capitalize" data-testid="intake-status-value">
                  {data.intake ? data.intake.status?.replace("_", " ") : "Not started"}
                </p>
              </CardContent>
            </Card>
            <Card data-testid="documents-count-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <Upload className="w-5 h-5 text-primary" />
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Documents</p>
                </div>
                <p className="mt-3 text-xl font-heading font-semibold" data-testid="documents-count-value">{data.document_count ?? 0} uploaded</p>
              </CardContent>
            </Card>
            <Card data-testid="next-appointment-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <CalendarDays className="w-5 h-5 text-primary" />
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Next Appointment</p>
                </div>
                <p className="mt-3 text-xl font-heading font-semibold" data-testid="next-appointment-value">
                  {data.upcoming_appointments?.[0] ? `${data.upcoming_appointments[0].date} · ${data.upcoming_appointments[0].time_slot}` : "None scheduled"}
                </p>
              </CardContent>
            </Card>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card data-testid="get-started-card">
              <CardHeader><CardTitle className="font-heading text-xl">Get started</CardTitle></CardHeader>
              <CardContent className="space-y-4 text-sm">
                {[
                  { done: !!data.intake, label: "Complete your intake form & e-signature", to: "/app/intake", testid: "action-intake" },
                  { done: (data.document_count ?? 0) > 0, label: "Upload your tax documents", to: "/app/intake", testid: "action-documents" },
                  { done: (data.upcoming_appointments || []).length > 0, label: "Book an appointment with our team", to: "/app/appointments", testid: "action-book" },
                ].map((step) => (
                  <Link key={step.label} to={step.to} data-testid={step.testid}
                    className="flex items-center gap-3 p-3 rounded-md border border-border hover:bg-muted/50 transition-colors">
                    <CheckCircle2 className={`w-5 h-5 ${step.done ? "text-primary" : "text-muted-foreground/40"}`} />
                    <span className={step.done ? "line-through text-muted-foreground" : ""}>{step.label}</span>
                    <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground" />
                  </Link>
                ))}
              </CardContent>
            </Card>
            <Card data-testid="client-appointments-card">
              <CardHeader><CardTitle className="font-heading text-xl">Your appointments</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {(data.upcoming_appointments || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">Nothing scheduled yet. <Link to="/app/appointments" className="text-primary underline underline-offset-2" data-testid="book-first-link">Book now</Link></p>
                )}
                {(data.upcoming_appointments || []).map((a) => (
                  <div key={a.appointment_id} className="flex items-center justify-between border-b border-border pb-3 last:border-0" data-testid={`client-appt-${a.appointment_id}`}>
                    <div>
                      <p className="text-sm font-medium">{a.team_member_name}</p>
                      <p className="text-xs text-muted-foreground">{a.date} at {a.time_slot}</p>
                    </div>
                    <Badge className="capitalize">{a.status}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
          <ReminderPreferences />
        </>
      )}
    </div>
  );
}
